import { useState } from 'react';
import { X, Plus } from '../../design-system/icons';
import { Badge } from '../../components/ui/badge';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/popover';
import { TruncationTooltip } from '../../components/common/TruncationTooltip';
import { ActionConfirmDialog } from '../../components/common/ActionConfirmDialog';
import { useIsTruncated } from '../../lib/useIsTruncated';
import { ProductPickerList, type ProductLite } from './ProductPickerList';

export interface ProductAssignCellProps {
  /** The creator's currently-assigned products (resolved — id + name). */
  assigned: ProductLite[];
  /** The campaign's product catalog — the popover's checkbox options. */
  campaignProducts: ProductLite[];
  /** Admin can edit (+ / ×); brand sees read-only chips. */
  canEdit: boolean;
  /** Called with the full desired id set (full-replace). */
  onAssign: (productIds: string[]) => void;
  /** Mutation in flight — disables interaction. */
  pending?: boolean;
  /** Replaces the read-only hover text when editing is blocked for a row-specific reason. */
  disabledReason?: string;
}

/**
 * ProductAssignCell — the Products column cell on the admin Finalized table
 * (Multi-Product Campaigns). Admin: removable chips (×) + a "+" popover of the
 * campaign's products as checkboxes (toggling posts the FULL id set —
 * full-replace). Brand: read-only chips with an "Assigned by the Discovr team"
 * tooltip. Empty is valid → "Assign products" (admin) / "—" (brand). The column
 * only renders when the campaign has >= 2 products (gated by the caller).
 *
 * The column has a fixed width (see ConfirmedTab's column def) — more chips
 * than fit on one line are clipped (never wrapped to a second row, which
 * would blow up row height unpredictably per creator). The "+" trigger sits
 * OUTSIDE the clipped chips box, pinned at the row's end, so it stays
 * reachable no matter how many chips overflow — and since its popover lists
 * every campaign product with a checkbox reflecting assignment, a chip that
 * got clipped out of view is still removable there. When clipped, hovering
 * the row shows every assigned product name as wrapped text (see
 * useIsTruncated — same "tooltip only when actually cut off" pattern as the
 * Creators column's name).
 */
export function ProductAssignCell({
  assigned,
  campaignProducts,
  canEdit,
  onAssign,
  pending,
  disabledReason,
}: ProductAssignCellProps) {
  const [open, setOpen] = useState(false);
  // Removing an assignment is confirmed first; adding is instant. Holds the
  // product awaiting an unassign-confirm (null = no dialog).
  const [pendingRemove, setPendingRemove] = useState<ProductLite | null>(null);
  const assignedIds = new Set(assigned.map((p) => p.product_id));
  const fullNames = assigned.map((p) => p.name).join(', ');
  const [rowRef, isTruncated] = useIsTruncated<HTMLDivElement>(fullNames);

  const addProduct = (id: string) => onAssign([...assignedIds, id]);
  const requestRemove = (product: ProductLite) => {
    setOpen(false); // close the popover so the confirm isn't stacked over it
    setPendingRemove(product);
  };
  const confirmRemove = () => {
    if (pendingRemove) {
      onAssign(assigned.filter((p) => p.product_id !== pendingRemove.product_id).map((p) => p.product_id));
    }
    setPendingRemove(null);
  };

  const row = (
    <div
      className="flex w-full min-w-0 items-center gap-1.5"
      // Only when NOT truncated — the truncation tooltip below takes over
      // (and showing full names) once the row is clipped, so there's no
      // double/competing hover tooltip on the same element.
      title={!canEdit && !isTruncated ? (disabledReason ?? 'Assigned by the Discovr team') : undefined}
    >
      {/* Chips clip inside THIS box (it shrinks; the "+" trigger below is a
          sibling with shrink-0), so the trigger can never be pushed past the
          clipped edge and become unreachable. */}
      <div ref={rowRef} className="relative flex min-w-0 items-center gap-1.5 overflow-hidden">
        {assigned.map((p) =>
          canEdit ? (
            <Badge key={p.product_id} variant="secondary" className="h-[22px] shrink-0 gap-1 pr-1">
              {p.name}
              <button
                type="button"
                onClick={() => requestRemove(p)}
                disabled={pending}
                aria-label={`Unassign ${p.name}`}
                className="rounded-full p-0.5 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
              >
                <X size={11} weight="bold" />
              </button>
            </Badge>
          ) : (
            <Badge key={p.product_id} variant="secondary" className="h-[22px] shrink-0">
              {p.name}
            </Badge>
          ),
        )}

        {!canEdit && assigned.length === 0 && (
          <span className="text-caption-regular text-muted-foreground">—</span>
        )}

        {/* Fade the clipped edge instead of a hard cut — only rendered once
            content actually overflows the fixed-width row. */}
        {isTruncated && (
          <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-linear-to-r from-transparent to-card" />
        )}
      </div>

      {canEdit && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={pending}
              aria-label="Assign products"
              // h-[22px]: same rendered height as a Badge (16px line-height +
              // 2×2px padding + 2×1px border) so the icon-only pill next to
              // text-bearing pills doesn't look mismatched.
              className="flex h-[22px] shrink-0 items-center gap-1 rounded-full border border-dashed border-border px-2 text-caption-medium text-primary transition-colors hover:bg-muted disabled:opacity-50"
            >
              <Plus size={11} weight="bold" />
              {assigned.length === 0 ? <span>Assign products</span> : null}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-56 p-1.5">
            <ProductPickerList
              products={campaignProducts}
              selectedIds={assignedIds}
              onToggle={(p) => (assignedIds.has(p.product_id) ? requestRemove(p) : addProduct(p.product_id))}
              disabled={pending}
            />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );

  return (
    <>
      <TruncationTooltip
        isTruncated={isTruncated}
        content={fullNames}
        contentClassName="max-w-[200px] text-left whitespace-normal"
      >
        {row}
      </TruncationTooltip>

      {pendingRemove ? (
        <ActionConfirmDialog
          open
          variant="delete"
          title="Unassign product?"
          description={`Remove “${pendingRemove.name}” from this creator? You can re-assign it anytime.`}
          confirmLabel="Unassign"
          busy={pending}
          onOpenChange={(o) => {
            if (!o) setPendingRemove(null);
          }}
          onConfirm={confirmRemove}
        />
      ) : null}
    </>
  );
}
